import type { UIEvent } from 'react';
import { useCallback } from 'react';
import type { ApolloQueryResult } from '@apollo/client';
import type {
  GetWorkspacesBasicQuery,
  GetWorkspacesBasicQueryResult,
  GetWorkspacesListQueryResult,
} from '@frameio/graphql-client/generated';
import type { FetchNextInput } from '@frameio/graphql-client/utils/useCursorPagination';

type UsePaginatedWorkspacesProps = {
  data?: GetWorkspacesBasicQuery;
  loading: boolean;
  fetchNext?: (
    input: FetchNextInput,
    newVariables?: Partial<
      | GetWorkspacesBasicQueryResult['variables']
      | GetWorkspacesListQueryResult['variables']
    >
  ) => Promise<
    ApolloQueryResult<
      | GetWorkspacesBasicQueryResult['data']
      | GetWorkspacesListQueryResult['data']
    >
  > | null;
  fetchCount?: number;
  fetchBoundaryMultiplier?: number;
};
export const usePaginatedWorkspacesLegacy = ({
  data,
  loading,
  fetchNext,
  fetchCount = 100,
  fetchBoundaryMultiplier = 0.4,
}: UsePaginatedWorkspacesProps) => {
  const handleFetchNext = useCallback(() => {
    if (!loading && fetchNext) {
      fetchNext({
        count: fetchCount,
        pageInfo: data?.account?.workspaces?.pageInfo || { hasNextPage: false },
      });
    }
  }, [fetchNext, loading, data, fetchCount]);

  const handleFetchNextOnScroll = useCallback(
    async (event: UIEvent<HTMLElement>) => {
      const scrollTop = event.currentTarget?.scrollTop;

      const scrollPosition = event.currentTarget?.offsetHeight + scrollTop;
      const fetchBoundary =
        event.currentTarget?.scrollHeight * fetchBoundaryMultiplier;
      const hasScrolledPastFetchBoundary = scrollPosition >= fetchBoundary;

      if (hasScrolledPastFetchBoundary) {
        handleFetchNext();
      }
    },
    [handleFetchNext, fetchBoundaryMultiplier]
  );

  return {
    handleFetchNextOnScroll,
    handleFetchNext,
    hasNextPage: data?.account?.workspaces?.pageInfo?.hasNextPage ?? false,
  };
};
